/**
 * Undo for the actions that move a case or change who holds it. Section 6 of
 * the request of 5 October 2026.
 *
 * After Complete and Send, Mark done, a flag, taking a case, an assignment, a
 * rework or a move between departments, a toast offers Undo for about ten
 * seconds. The confirmation before the action stays; Undo is the safety net
 * after it.
 *
 * The server does the undoing and decides whether it may be done: it puts the
 * case back where it was, with whoever held it, its clock and the work that was
 * open, and marks the Lab Notes entry undone rather than deleting it. Only the
 * person who took the action, only for a moment, and only while nobody else has
 * acted on the case since. This file is the vocabulary both halves share: what
 * an action answers with, how long the screen offers it, and the refusals, which
 * the screen renders verbatim.
 */

/**
 * What an action answers with when it can be taken back: the id to send, and
 * the moment the server stops accepting it. On the case, for its actor only.
 */
export interface UndoRef {
  id: string;
  /** ISO 8601 UTC. */
  until: string;
}

/** How long the toast offers Undo. The client's "about 10 seconds". */
export const UNDO_OFFER_MS = 10_000;

/**
 * How long the server accepts an undo: longer than the toast offers it, so a
 * tap in its last second still arrives in time over a slow connection.
 */
export const UNDO_WINDOW_SECONDS = 30;

/** The server's deadline for an action taken at this moment. */
export function undoDeadline(takenAt: Date): string {
  return new Date(takenAt.getTime() + UNDO_WINDOW_SECONDS * 1000).toISOString();
}

/**
 * The refusals, worded once. The server says them, the mock says them the same
 * way, and the toast shows whichever arrives.
 */
export const UNDO_REFUSAL = {
  notFound: 'There is nothing to undo.',
  notYours: 'Only the person who took an action can undo it.',
  tooLate: 'It is too late to undo this.',
  alreadyUndone: 'This was already undone.',
  actedSince: 'Somebody has acted on this case since, so it can no longer be undone.',
  leftTheLab: 'This case has left the lab, so it can no longer be undone.',
} as const;

/**
 * Why this person cannot undo this action now, or null when they can. The case
 * names the one action still open to undo, and any later change to the case
 * clears it, so a case that names another action, or none, has been acted on
 * since.
 */
export function undoRefusal(input: {
  undoId: string;
  byUserId: string;
  expiresAt: string;
  undone: boolean;
  callerId: string;
  /** The undo the case names now, or null. */
  currentUndoId: string | null;
  now: Date;
}): string | null {
  if (input.byUserId !== input.callerId) return UNDO_REFUSAL.notYours;
  if (input.undone) return UNDO_REFUSAL.alreadyUndone;
  if (input.currentUndoId !== input.undoId) return UNDO_REFUSAL.actedSince;
  if (input.now.getTime() > new Date(input.expiresAt).getTime()) return UNDO_REFUSAL.tooLate;
  return null;
}

/** The activity log's sentence for an undo. The log is append only, so it adds a row. */
export function undoLogSentence(summary: string): string {
  return `undid: ${summary}`;
}

/** What a person told about the action is told when it is taken back. */
export function undoToldSentence(jobOrderNo: string, byName: string): string {
  return `${byName} undid the last change to ${jobOrderNo}`;
}
